/**
 * GuestLinkSheet — iter197.
 *
 * Admin-side dialog to mint a magic link and share it with a guest. Two
 * variants controlled by `scope`:
 *   • scope='rsvp'  — single-session RSVP join link (48h)
 *   • scope='trip'  — read-only trip summary link (default 30d)
 *
 * On mint we show the URL with copy + native share + WhatsApp deep-link.
 */
import { useCallback, useEffect, useState } from 'react';
import { Copy, Share2, MessageCircle, Loader2, CheckCircle2, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

export default function GuestLinkSheet({
  open,
  onClose,
  scope,             // 'rsvp' | 'trip'
  groupId,
  sessionId,         // required when scope='rsvp'
  groupName,
  sessionTitle,
}) {
  const [guestName, setGuestName] = useState('');
  const [note, setNote] = useState('');
  const [minting, setMinting] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);  // { token, url, expires_at, scope }

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setGuestName('');
      setNote('');
      setLinkInfo(null);
    }
  }, [open]);

  const onMint = useCallback(async () => {
    setMinting(true);
    try {
      const endpoint = scope === 'rsvp'
        ? `/groups/${groupId}/sessions/${sessionId}/guest-rsvp-link`
        : `/groups/${groupId}/trip-view-link`;
      const r = await api.post(endpoint, {
        guest_name: guestName.trim() || undefined,
        note: note.trim() || undefined,
      });
      const path = scope === 'rsvp' ? `/guest/rsvp/${r.data.token}` : `/guest/trip/${r.data.token}`;
      const url = `${window.location.origin}${path}`;
      setLinkInfo({ ...r.data, url });
      toast.success('Link ready to share');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mint link');
    } finally {
      setMinting(false);
    }
  }, [scope, groupId, sessionId, guestName, note]);

  const onCopy = useCallback(async () => {
    if (!linkInfo?.url) return;
    try {
      await navigator.clipboard.writeText(linkInfo.url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy');
    }
  }, [linkInfo]);

  const onShare = useCallback(async () => {
    if (!linkInfo?.url) return;
    const text = scope === 'rsvp'
      ? `${guestName ? guestName + ', ' : ''}you're invited to ${sessionTitle || 'a session'} at ${groupName}. RSVP without an account:`
      : `Here's the trip summary for ${groupName}:`;
    try {
      if (navigator.share) {
        await navigator.share({ title: scope === 'rsvp' ? 'You\'re invited' : 'Trip view', text, url: linkInfo.url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${linkInfo.url}`);
        toast.success('Copied — paste anywhere');
      }
    } catch {
      /* user cancelled */
    }
  }, [linkInfo, scope, guestName, sessionTitle, groupName]);

  const onWhatsApp = useCallback(() => {
    if (!linkInfo?.url) return;
    const text = scope === 'rsvp'
      ? `${guestName ? '*Hi ' + guestName + '* — ' : ''}you're invited to *${sessionTitle || 'a session'}* at *${groupName}*. RSVP without an account:\n${linkInfo.url}`
      : `Here's the *${groupName}* trip summary:\n${linkInfo.url}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, '_blank', 'noopener,noreferrer');
  }, [linkInfo, scope, guestName, sessionTitle, groupName]);

  const heading = scope === 'rsvp' ? 'Share RSVP link with a guest' : 'Share read-only trip view';
  const sub = scope === 'rsvp'
    ? 'Generate a 48-hour link the guest can use to join the headcount — no account needed.'
    : 'Generate a 30-day link to share the trip dashboard with someone who isn\'t on SplitEasy.';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid={`guest-link-sheet-${scope}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="w-4 h-4 text-primary" />
            {heading}
          </DialogTitle>
          <DialogDescription>{sub}</DialogDescription>
        </DialogHeader>

        {!linkInfo ? (
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="guest-link-name" className="text-xs font-semibold uppercase tracking-wider">Guest name (optional)</Label>
              <Input
                id="guest-link-name"
                placeholder="e.g. Aman Patel"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                className="mt-1 text-base"
                data-testid="guest-link-name-input"
              />
            </div>
            <div>
              <Label htmlFor="guest-link-note" className="text-xs font-semibold uppercase tracking-wider">Personal note (optional)</Label>
              <Input
                id="guest-link-note"
                placeholder="e.g. Come early to warm up"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1 text-base"
                data-testid="guest-link-note-input"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-2" data-testid="guest-link-result">
            <div className="rounded-xl border-2 border-emerald-500/30 bg-emerald-500/10 p-3 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <p className="text-emerald-700 dark:text-emerald-300 font-semibold">
                Link minted. Expires {new Date(linkInfo.expires_at).toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
              <p className="font-mono text-xs break-all" data-testid="guest-link-url">{linkInfo.url}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" size="sm" onClick={onCopy} data-testid="guest-link-copy-btn" className="gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy
              </Button>
              <Button variant="outline" size="sm" onClick={onShare} data-testid="guest-link-share-btn" className="gap-1.5">
                <Share2 className="w-3.5 h-3.5" /> Share
              </Button>
              <Button variant="outline" size="sm" onClick={onWhatsApp} data-testid="guest-link-whatsapp-btn" className="gap-1.5 text-green-600 hover:text-green-700">
                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="guest-link-close-btn">
            {linkInfo ? 'Done' : 'Cancel'}
          </Button>
          {!linkInfo && (
            <Button onClick={onMint} disabled={minting} data-testid="guest-link-mint-btn" className="gap-1.5">
              {minting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LinkIcon className="w-3.5 h-3.5" />}
              {minting ? 'Minting…' : 'Generate link'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
